/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	createBrowserRouter,
	RouterProvider,
} from 'react-router';

// Routes
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Constants
import { paths } from '@config/constants/paths';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                               Direct imports                                */
/* -------------------------------------------------------------------------- */

// Core
import MainPage from '@app/routes/app/main/main';
import ErrorPage from '@app/routes/error/error-page';

// Auth
import AuthArecPage from '@app/routes/auth-arec/login/login';
import UpdatePasswordArecPage from '@app/routes/auth-arec/update-password/update-password';
import AuthPage from '@app/routes/auth/login/login';
import UpdateEmailPage from '@app/routes/auth/update-email/update-email';
import UpdatePasswordPage from '@app/routes/auth/update-password/update-password';

// App
import AccountSettingsPage from '@app/routes/app/account-settings/account-settings';
import DashboardPage from '@app/routes/app/dashboard/dashboard';
import OffsetsPage from '@app/routes/app/offsets/offsets';
import AdditionalitiesPage from '@app/routes/app/resources/additionalities/additionalities';
import ApiTokensPage from '@app/routes/app/resources/api-tokens/api-tokens';
import BrandsPage from '@app/routes/app/resources/brands/brands';
import ConversionFactorPage from '@app/routes/app/resources/conversion-factors/conversion-factors';
import MeasurabilitiesPage from '@app/routes/app/resources/measurabilities/measurabilities';
import MeterPointsPage from '@app/routes/app/resources/meter-points/meter-points';
import OrganizationsPage from '@app/routes/app/resources/organizations/organizations';
import ProducersPage from '@app/routes/app/resources/producers/producers';
import SitesPage from '@app/routes/app/resources/sites/sites';
import UsersPage from '@app/routes/app/resources/users/users';
import ImportHistoryPage from '@app/routes/app/sourcing/import-history/import-history';
import MintHistoryPage from '@app/routes/app/sourcing/mint-history/mint-history';
import ProductionsPage from '@app/routes/app/sourcing/productions/productions';

// Catalogs (app)
import AdditionalityProjectDetailsPage from '@app/routes/app/catalogs/additionality-projects/details/additionality-project-details';
import AdditionalityProjectsPage from '@app/routes/app/catalogs/additionality-projects/list/additionality-projects';
import MeasurabilityProjectDetailsPage from '@app/routes/app/catalogs/measurability-projects/details/measurability-project-details';
import MeasurabilityProjectsPage from '@app/routes/app/catalogs/measurability-projects/list/measurability-projects';

// Catalogs (public)
import PublicAdditionalityProjectDetailsPage from '@app/routes/public/catalogs/additionality-project-details/additionality-project-details';
import PublicAdditionalityProjectsPage from '@app/routes/public/catalogs/additionality-projects/additionality-projects';
import PublicMeasurabilityProjectDetailsPage from '@app/routes/public/catalogs/measurability-project-details/measurability-project-details';
import PublicMeasurabilityProjectsPage from '@app/routes/public/catalogs/measurability-projects/measurability-projects';
import { ACLS } from '@config/constants/acls';

// CGU (public)
import CGU from '@app/routes/public/cgu/cgu';
import CGUEN from '@app/routes/public/cgu/cgu-en';
import CGUFR from '@app/routes/public/cgu/cgu-fr';

/* -------------------------------------------------------------------------- */
/*                                    Router                                  */
/* -------------------------------------------------------------------------- */

const router = createBrowserRouter([
	{
		path: paths.app.root.path,
		element: (
			<ProtectedRoute>
				<MainPage />
			</ProtectedRoute>
		),
		errorElement: <ErrorPage />,
		children: [
			{
				path: paths.app.dashboard.path,
				element: (
					<ProtectedRoute>
						<DashboardPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.accountSettings.path,
				element: (
					<ProtectedRoute>
						<AccountSettingsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.measurabilities.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.MEASURABILITIES.LIST]}
					>
						<MeasurabilitiesPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.additionalities.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.ADDITIONALITIES.LIST]}
					>
						<AdditionalitiesPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.productions.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]}
					>
						<ProductionsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.mintHistory.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.MINT_HISTORIES.LIST]}
					>
						<MintHistoryPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.importHistory.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.IMPORT_HISTORIES.LIST]}
					>
						<ImportHistoryPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.offsets.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.USER,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]}
						acls={[ACLS.OFFSETS.LIST]}
					>
						<OffsetsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.sites.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.SITES.LIST]}
					>
						<SitesPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.apiTokens.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.PRODUCER_ADMIN,
						]}
						acls={[ACLS.API_TOKENS.LIST]}
					>
						<ApiTokensPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.meterPoints.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.METER_POINTS.LIST]}
					>
						<MeterPointsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.producers.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
							ROLES.STAFF,
						]}
					>
						<ProducersPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.brands.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
						]}
					>
						<BrandsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.users.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.USERS.LIST]}
					>
						<UsersPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.organizations.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.ORGANIZATIONS.LIST]}
					>
						<OrganizationsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.conversionFactors.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
						acls={[ACLS.CONVERSION_FACTORS.LIST]}
					>
						<ConversionFactorPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.measurabilityProjects.path,
				element: (
					<ProtectedRoute>
						<MeasurabilityProjectsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.measurabilityProjectDetails.path,
				element: (
					<ProtectedRoute>
						<MeasurabilityProjectDetailsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.additionalityProjects.path,
				element: (
					<ProtectedRoute>
						<AdditionalityProjectsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.additionalityProjectDetails.path,
				element: (
					<ProtectedRoute>
						<AdditionalityProjectDetailsPage />
					</ProtectedRoute>
				),
			},
		],
	},

	/* ------------------------------ Public Routes ----------------------------- */
	{
		path: paths.public.cgu.path,
		element: <CGU />,
	},
	{
		path: paths.public.cgu.path,
		element: <CGU />,
	},
	{
		path: paths.public.cguEn.path,
		element: <CGUEN />,
	},
	{
		path: paths.public.cguFr.path,
		element: <CGUFR />,
	},
	{
		path: paths.public.catalogMeasurabilityProjects.path,
		element: <PublicMeasurabilityProjectsPage />,
	},
	{
		path: paths.public.catalogMeasurabilityProjectDetails
			.path,
		element: <PublicMeasurabilityProjectDetailsPage />,
	},
	{
		path: paths.public.catalogAdditionalityProjects.path,
		element: <PublicAdditionalityProjectsPage />,
	},
	{
		path: paths.public.catalogAdditionalityProjectDetails
			.path,
		element: <PublicAdditionalityProjectDetailsPage />,
	},

	/* ------------------------------- Auth Routes ------------------------------- */
	{
		path: paths.auth.login.path,
		element: (
			<PublicRoute>
				<AuthPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.loginArec1.path,
		element: (
			<PublicRoute>
				<AuthArecPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.loginArec2.path,
		element: (
			<PublicRoute>
				<AuthArecPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.signIn.path,
		element: (
			<PublicRoute>
				<UpdatePasswordPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.updatePasswordArec.path,
		element: (
			<PublicRoute>
				<UpdatePasswordArecPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.updateEmail.path,
		element: <UpdateEmailPage />,
	},
]);

/* -------------------------------------------------------------------------- */
/*                                App Router                                   */
/* -------------------------------------------------------------------------- */

const AppRouter = () => (
	<RouterProvider router={router} />
);

export default AppRouter;
